import { ButtonPrimary, ButtonSecondary } from 'components/Button'
import React, { useState } from 'react'
import styled from 'styled-components'
import { Text } from 'rebass'
import { REFERRAL_FOR_USERS_PARAMS } from 'pages/utils/ReferralHandler'
import { useActiveWeb3React } from 'hooks'
import { useWalletModalToggle } from 'state/application/hooks'

const RefContainer = styled.div`
  padding: 32px;
  display: flex;
  border-radius: 16px;
  background: ${({ theme }) => theme.bg0};
  flex-direction: column;
  @media (max-width: 768px) {
    padding: 24px;
  }
`

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;

  h2 {
    margin-top: 0;
    margin-bottom: 24px;
    font-family: Unbounded;
    font-size: 22px;
    @media (max-width: 768px) {
      font-size: 16px;
      margin-bottom: 16px;
    }
  }

  span {
    font-size: 16px;
    color: ${({ theme }) => theme.text2};
  }

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 24px;
    .referral-link {
      width: 100%;
    }
  }
`

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-top: 32px;
`

const ListItem = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;

  .address {
    color: ${({ theme }) => theme.text1};
  }

  .date {
    color: ${({ theme }) => theme.text2};
    font-size: 12px;
  }

  padding-bottom: 24px;
  border-bottom: 1px solid ${({ theme }) => theme.text3};
`

const EmptyState = styled.div`
  font-size: 16px;
  color: ${({ theme }) => theme.text2};
  display: flex;
  flex-direction: column;
  gap: 12px;
  justify-content: center;
  text-align: center;
  align-items: center;
  padding: 24px 12px;
  border-radius: 16px;
  background: ${({ theme }) => theme.bg2};
`

function formatTimestamp(timestamp: number) {
  const date = new Date(timestamp * 1000)
  const options: Intl.DateTimeFormatOptions = {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false, // 24-hour format
  }

  return new Intl.DateTimeFormat('en-US', options).format(date).replace(',', '')
}

const Ref: React.FC<{ referrals: any[]; username: null | string; openModal: () => void }> = ({
  referrals,
  username,
  openModal,
}) => {
  const { account } = useActiveWeb3React()
  const toggleWalletModal = useWalletModalToggle()
  const [copyStatus, setCopyStatus] = useState<string | null>(null) // State to manage copy status
  const saveLink = () => {
    if (username) {
      const referralLink = `${window.location.origin}/#/swap?${REFERRAL_FOR_USERS_PARAMS}=${username}`
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard
          .writeText(referralLink)
          .then(() => {
            setCopyStatus('Copied!')
            setTimeout(() => setCopyStatus(null), 5000)
          })
          .catch(() => {
            // Fallback to manual copy if writeText fails
            fallbackCopyTextToClipboard(referralLink)
          })
      } else {
        // Fallback for older browsers
        fallbackCopyTextToClipboard(referralLink)
      }
    }
  }

  // Fallback function using document.execCommand('copy')
  const fallbackCopyTextToClipboard = (text: string) => {
    const textArea = document.createElement('textarea')
    textArea.value = text
    textArea.style.position = 'fixed' // Prevent scrolling to bottom of page in Microsoft Edge.
    document.body.appendChild(textArea)
    textArea.focus()
    textArea.select()

    try {
      const successful = document.execCommand('copy')
      if (successful) {
        setCopyStatus('Copied!')
      } else {
        setCopyStatus('Copy failed')
      }
    } catch (err) {
      console.error('Fallback: Oops, unable to copy', err)
    }

    document.body.removeChild(textArea)
    setTimeout(() => setCopyStatus(null), 5000)
  }

  return (
    <RefContainer>
      <HeaderContainer>
        <div>
          <h2>Referrals</h2>
          <span>Find here the list of users referred through your referral link.</span>
        </div>
        <div className="referral-link">
          {account ? (
            <>
              {' '}
              {username ? (
                <ButtonSecondary id="copy" style={{ padding: 16 }} as={'button'} onClick={saveLink}>
                  <Text fontWeight={400} fontSize={14} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {copyStatus || 'Copy my referral link'}
                  </Text>
                </ButtonSecondary>
              ) : (
                <ButtonSecondary onClick={() => openModal()}>Sign up</ButtonSecondary>
              )}
            </>
          ) : (
            <>
              <ButtonPrimary onClick={() => toggleWalletModal()}>Connect Wallet</ButtonPrimary>
            </>
          )}
        </div>
      </HeaderContainer>

      <ListContainer>
        {referrals.length === 0 ? (
          <EmptyState>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="icon icon-tabler icons-tabler-outline icon-tabler-mood-empty"
            >
              <path stroke="none" d="M0 0h24v24H0z" fill="none" />
              <path d="M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0" />
              <path d="M9 10l.01 0" />
              <path d="M15 10l.01 0" />
              <path d="M9 15l6 0" />
            </svg>
            <span>You haven’t referred anyone yet.</span>
            <span>Copy your referral link and start sharing it to begin earning referrals!</span>
          </EmptyState>
        ) : (
          <>
            {referrals.map((ref: any) => {
              return (
                <ListItem key={ref.address}>
                  <div className="address" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="icon icon-tabler icons-tabler-outline icon-tabler-users-plus"
                    >
                      <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                      <path d="M5 7a4 4 0 1 0 8 0a4 4 0 0 0 -8 0" />
                      <path d="M3 21v-2a4 4 0 0 1 4 -4h4c.96 0 1.84 .338 2.53 .901" />
                      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                      <path d="M16 19h6" />
                      <path d="M19 16v6" />
                    </svg>
                    <span style={{ fontSize: '16px' }}>{`${ref.address.slice(0, 4)}...${ref.address.slice(-3)}`}</span>
                  </div>
                  <span className="date">joined on {formatTimestamp(ref.createdAt)}</span>
                </ListItem>
              )
            })}
          </>
        )}
      </ListContainer>
    </RefContainer>
  )
}
export default Ref
